/**
 * Programme indicators for this facility, calculated on the device from the records it holds
 * (the whole facility's records are synced to each device).
 */
import {
  type DashboardTile,
  ETH_MONTHS_EN,
  type IndicatorResult,
  type MeasureResult,
  type Pack,
  type PatientBundle,
  type Row,
  addDays,
  anomaly,
  computeIndicator,
  computeMeasure,
  ethToIso,
  tileFigure,
} from '@assist/core';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { loadBundles } from '../care.js';
import { clock } from '../controller.js';
import { eth } from '../dates.js';
import { useApp } from '../hooks.js';
import { t } from '../i18n.js';
import { activePacks } from '../packs.js';
import { Icon } from '../ui/icons.js';

export function Numbers() {
  const a = useApp();
  const [bundles, setBundles] = useState<PatientBundle[] | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reload when the store changes
  useEffect(() => {
    let live = true;
    void loadBundles(true).then((b) => live && setBundles(b));
    return () => {
      live = false;
    };
  }, [a.status]);
  const packs = activePacks(a.packSettings).map((x) => x.pack);
  const today = clock.today();
  if (!packs.length)
    return (
      <div class="card">
        <h3>{t.numbersTitle}</h3>
        <p class="muted">{t.noPacks}</p>
      </div>
    );
  return (
    <section>
      <div class="card">
        <div class="dhead">
          <div>
            <h3>{t.numbersTitle}</h3>
            <p class="muted">{t.numbersHelp(eth(today))}</p>
          </div>
          <a class="sec dhis" href="#/hmis">
            <Icon k="registers" size={18} />
            {t.hmisOpen}
          </a>
        </div>
        {!bundles ? <p class="muted">{t.computing}</p> : null}
      </div>
      {bundles
        ? packs.flatMap((pack) =>
            pack.programmes.map((prog) => {
              const defs = pack.indicators.filter((i) => i.programme === prog.code);
              if (!defs.length) return null;
              return (
                <div class="card" key={`${pack.id}/${prog.code}`}>
                  <h3>{prog.display}</h3>
                  <div class="dkpis">
                    {defs.map((d) => {
                      const r = computeIndicator(d, bundles, today);
                      const src = d.sources
                        .map((s) => {
                          const ps = pack.sources.find((x) => x.id === s.source);
                          return `${ps?.title ?? s.source}, ${s.locator}`;
                        })
                        .join('; ');
                      return <Tile key={d.id} r={r} description={d.description} source={src} />;
                    })}
                  </div>
                </div>
              );
            }),
          )
        : null}
      {bundles
        ? packs
            .filter((p) => p.dashboard && p.measures?.length)
            .map((p) => <Overview key={p.id} pack={p} bundles={bundles} />)
        : null}
    </section>
  );
}

/** A pack's facility overview: themed tiles over a chosen period, and six-month trends. */
function Overview({ pack, bundles }: { pack: Pack; bundles: PatientBundle[] }) {
  const [days, setDays] = useState(0);
  const today = clock.today();
  const measures = pack.measures ?? [];
  const dash = pack.dashboard;
  // biome-ignore lint/correctness/useExhaustiveDependencies: a fresh cache for each set of records
  const cache = useMemo(() => new Map<string, Row[]>(), [bundles]);
  const results = useMemo(() => {
    const from = days ? addDays(today, -days + 1) : null;
    return new Map<string, MeasureResult>(
      measures.map((m) => [m.id, computeMeasure(m, bundles, from, today, cache)]),
    );
  }, [measures, bundles, days, today, cache]);
  // the last six Ethiopian months, this one included
  const months = useMemo(() => {
    const e = clock.todayEth();
    const out: Array<{ label: string; from: string; to: string }> = [];
    let y = e.year;
    let m = e.month > 12 ? 12 : e.month;
    for (let i = 0; i < 6; i++) {
      out.unshift({
        label: (ETH_MONTHS_EN[m - 1] ?? '').slice(0, 3),
        from: ethToIso({ year: y, month: m, day: 1 }) ?? '',
        to: ethToIso({ year: y, month: m, day: 30 }) ?? '',
      });
      m -= 1;
      if (m < 1) {
        m = 12;
        y -= 1;
      }
    }
    return out;
  }, []);
  const trend = (tile: DashboardTile) =>
    months.map((mo) => {
      const r = new Map<string, MeasureResult>();
      for (const id of [tile.value, tile.of].filter(Boolean) as string[]) {
        const def = measures.find((x) => x.id === id);
        if (def) r.set(id, computeMeasure(def, bundles, mo.from, mo.to, cache));
      }
      return tileFigure(tile, r).value;
    });
  if (!dash) return null;
  return (
    <div class="card" data-overview={pack.id}>
      <div class="dhead">
        <div>
          <h3>
            {t.overviewTitle} — {pack.short_title ?? pack.title}
          </h3>
          <p class="muted">{t.overviewHelp}</p>
        </div>
        <label class="noprint">
          {t.periodLabel}{' '}
          <select value={days} onChange={(e) => setDays(Number(e.currentTarget.value))}>
            {[0, 30, 90, 365].map((d) => (
              <option key={d} value={d}>
                {t.periods[d]}
              </option>
            ))}
          </select>
        </label>
      </div>
      {dash.trends?.length ? (
        <>
          <h4>{t.trendsTitle}</h4>
          <div class="regwrap">
            <table class="reg trend">
              <thead>
                <tr>
                  <th />
                  {months.map((m) => (
                    <th key={m.from}>{m.label}</th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {dash.trends.map((tr) => {
                  const series = trend(tr);
                  const flag = anomaly(series);
                  return (
                    <tr key={tr.title}>
                      <th>{tr.title}</th>
                      {series.map((v, i) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: months are positional
                        <td key={i}>{v === null ? '—' : v}</td>
                      ))}
                      <td class={flag && flag === tr.bad ? 'flag-bad' : ''}>
                        {flag ? `${flag === 'up' ? '▲' : '▼'} ${t.vsBaseline}` : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
      {dash.sections.map((sec) => (
        <div key={sec.title} class="ovsec">
          <h4>{sec.title}</h4>
          <div class="dkpis">
            {sec.tiles.map((tile) => {
              const res = results.get(tile.value);
              if (res?.groups) {
                const total = res.count || 1;
                return (
                  <div class="dkpi" key={tile.title} data-measure={tile.value}>
                    <div class="dk-top">{tile.title}</div>
                    {res.groups.length ? (
                      res.groups.map(([k, n]) => (
                        <div class={tile.good?.includes(k) ? 'ovbar good' : 'ovbar'} key={k}>
                          <span class="k">
                            {choiceLabel(pack, measures.find((m) => m.id === tile.value)?.group, k)}
                          </span>
                          <span class="bar">
                            <span style={{ width: `${Math.round((100 * n) / total)}%` }} />
                          </span>
                          <span>{n}</span>
                        </div>
                      ))
                    ) : (
                      <div class="dk-s">—</div>
                    )}
                  </div>
                );
              }
              const f = tileFigure(tile, results);
              return (
                <div class="dkpi" key={tile.title} data-measure={tile.value}>
                  <div class="dk-top">{tile.title}</div>
                  <div class="dk-v tile-value">
                    {f.value === null
                      ? '—'
                      : tile.of
                        ? `${f.value}${tile.per ? '' : '%'}`
                        : f.value}
                  </div>
                  <div class="dk-s">{tile.of ? t.ofN(f.num, f.den ?? 0) : (tile.sub ?? '')}</div>
                </div>
              );
            })}
          </div>
          {sec.note ? <p class="help">{sec.note}</p> : null}
        </div>
      ))}
    </div>
  );
}

const choiceLabel = (pack: Pack, concept: string | undefined, code: string) =>
  pack.concepts.find((c) => c.code === concept)?.choices?.find((x) => String(x.code) === code)
    ?.display ?? code;

function Tile({
  r,
  description,
  source,
}: { r: IndicatorResult; description: string; source: string }) {
  return (
    <div class="dkpi" data-indicator={r.id}>
      <div class="dk-top">
        <Icon k={r.kind === 'registered' ? 'register' : 'dashboard'} size={20} />
        {r.title}
      </div>
      <div class="dk-v tile-value">
        {r.kind === 'registered' ? r.numerator : r.percent === null ? '—' : `${r.percent}%`}
      </div>
      {r.percent !== null || r.kind !== 'registered' ? (
        <div class="dk-s">{t.ofN(r.numerator, r.denominator)}</div>
      ) : null}
      <details>
        <summary>{t.why}</summary>
        <p class="help">{description}</p>
        <p class="help">
          {t.source}: {source}
        </p>
      </details>
    </div>
  );
}
